package com.evoapps.evoshift.domain.provider

/**
 * SH-240: the campaign labels of the link someone came from before signing up (the product site
 * passes `utm_source`, `utm_campaign` and `ref` on to the web app and to Google Play's install
 * referrer). Sent once with the sign-up to the `recordSignupSource` function, which keeps them in
 * the account's private funnel document (docs/funnel.md). Labels only, never anything typed.
 */
data class SignupSource(
    val utmSource: String? = null,
    val utmCampaign: String? = null,
    val ref: String? = null
) {
    val isEmpty: Boolean get() = utmSource == null && utmCampaign == null && ref == null

    companion object {
        private const val MAX_LABEL = 64

        /**
         * The labels of a query string ("utm_source=a&utm_campaign=b&ref=c", with or without the
         * leading "?"; percent-decoded), cleaned like the server does; null when it names none.
         */
        fun fromQuery(query: String?): SignupSource? {
            if (query.isNullOrBlank()) return null
            val params = query.removePrefix("?").split('&').mapNotNull { part ->
                val key = part.substringBefore('=', "")
                if (key.isEmpty()) null else key to decode(part.substringAfter('=', ""))
            }.toMap()
            val source = SignupSource(
                utmSource = clean(params["utm_source"]),
                utmCampaign = clean(params["utm_campaign"]),
                ref = clean(params["ref"])
            )
            return source.takeUnless { it.isEmpty }
        }

        /** Lower case; letters, digits and . _ - only (others become _); at most 64 characters. */
        fun clean(value: String?): String? {
            val lower = value?.trim()?.lowercase()?.take(MAX_LABEL) ?: return null
            val label = lower.map { c -> if (c in 'a'..'z' || c in '0'..'9' || c == '.' || c == '_' || c == '-') c else '_' }
                .joinToString("")
            return label.takeIf { it.any { c -> c != '_' } }
        }

        private fun decode(value: String): String {
            val bytes = mutableListOf<Byte>()
            var i = 0
            while (i < value.length) {
                val c = value[i]
                when {
                    c == '+' -> { bytes.add(' '.code.toByte()); i++ }
                    c == '%' && i + 2 < value.length ->
                        value.substring(i + 1, i + 3).toIntOrNull(16)?.let { bytes.add(it.toByte()); i += 3 }
                            ?: run { bytes.add(c.code.toByte()); i++ }
                    else -> { c.toString().encodeToByteArray().forEach { bytes.add(it) }; i++ }
                }
            }
            return bytes.toByteArray().decodeToString()
        }
    }
}

/** Where this platform learns the [SignupSource] (the web app's address, Play's install referrer). */
fun interface SignupSourceProvider {
    /** Null when the app was not opened from a campaign link (or the platform cannot tell). */
    suspend fun signupSource(): SignupSource?

    companion object {
        val None = SignupSourceProvider { null }
    }
}
